import { Suspense, useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';

import { useOktaAuth } from '@okta/okta-react';
import * as Sentry from '@sentry/react';

import { AuthProvider } from '@/providers/AuthProvider.tsx';
import { useAppSelector } from '@/store/hooks';
import { useGetAllFlagsQuery } from '@/store/slices/api/featureFlagsApiSlice';

import { MainAppShell } from '@/containers/AppShell';
import LoadingPage from '@/pages/common/LoadingPage';

// Kicks off the feature flags fetch eagerly so the cache is warm for all useFeatureFlag calls,
// but does NOT block rendering — useFeatureFlag already returns defaultValue while loading.
const FeatureFlagsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const token = useAppSelector((state) => state.auth.token);
  const { refetch, isError } = useGetAllFlagsQuery(undefined, { skip: !token });
  const seenToken = useRef<string | null>(null);
  const retried = useRef(false);

  // A persisted session can fire this before the in-memory token is ready, or with a
  // stale token. Retry once the current token is in place so navigation flags apply.
  useEffect(() => {
    if (!token) return;
    const tokenChanged = seenToken.current !== null && seenToken.current !== token;
    if (tokenChanged) retried.current = false;
    seenToken.current = token;
    if ((tokenChanged || isError) && !retried.current) {
      retried.current = true;
      void refetch();
    }
  }, [token, isError, refetch]);

  return <>{children}</>;
};

const AuthGuard = () => {
  const { oktaAuth, authState } = useOktaAuth();
  const location = useLocation();
  const hasRedirected = useRef(false);

  // Handle redirect - only trigger once per mount lifecycle
  useEffect(() => {
    const initiateRedirect = async () => {
      if (!authState) {
        return;
      }

      if (!authState.isAuthenticated && !hasRedirected.current) {
        // Clear any stale transaction that might prevent redirect
        try {
          await oktaAuth.transactionManager.clear();
        } catch (e) {
          // Ignore errors when clearing transaction
        }

        // Check if we're already in a redirect flow, if so don't redirect again
        if (!hasRedirected.current) {
          hasRedirected.current = true;
          oktaAuth.setOriginalUri(location.pathname + location.search);

          Sentry.addBreadcrumb({
            category: 'auth',
            level: 'info',
            message: 'Calling signInWithRedirect (no existing transaction)',
            data: {
              pathname: location.pathname
            }
          });

          await oktaAuth.signInWithRedirect();
        }
      }
    };

    initiateRedirect();
  }, [authState, oktaAuth, location.pathname, location.search]);

  if (!authState) {
    return (
      <LoadingPage title="Authenticating" message="Please wait while we verify your identity..." />
    );
  }

  if (authState?.isAuthenticated) {
    return (
      <AuthProvider>
        <FeatureFlagsProvider>
          <MainAppShell>
            <Suspense
              fallback={
                <LoadingPage
                  title="Getting Things Ready"
                  message="Setting things up for you, hang tight..."
                />
              }>
              <Outlet />
            </Suspense>
          </MainAppShell>
        </FeatureFlagsProvider>
      </AuthProvider>
    );
  }

  // Show loading while redirect is in progress (handled by useEffect above)
  return (
    <LoadingPage
      title="Redirecting"
      message={"It seems you're not authenticated. Redirecting you to login..."}
    />
  );
};

export default AuthGuard;
